import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { CitationLink } from '../../components/ui/citation';
import { Paper, Box, Stack, Grid, Title as MantineTitle, Text, Group } from '@mantine/core';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { citations, districtComparisonData, shopTypeComparisonData, IconMapping } from '../../data';

const MarketAnalysisPanel: React.FC = () => {
    return (
        <Stack gap="lg">
            <Card>
                <CardHeader><CardTitle><Group gap="xs"><IconMapping.Building2 size={20} />廣州商業地產市場宏觀分析</Group></CardTitle></CardHeader>
                <CardContent pt="md">
                    <Grid>
                        <Grid.Col span={{ base: 12, md: 7 }}>
                            <MantineTitle order={4} mb="sm">市場供需與租金概況</MantineTitle>
                            <Text mb="sm">
                                根據世邦魏理仕(CBRE)發佈的《2024年廣州房地產市場回顧與展望》，2024年廣州整體商業地產市場（包括辦公樓及零售物業）的供應量較2023年有所下降。
                                然而，市場需求未能達到預期水平，導致業主普遍採取「以價換量」的租賃策略以維持入住率。
                            </Text>
                            <Text mb="sm">
                                這一趨勢使得各主要商業物業類型的租金普遍承壓並出現下跌。例如，報告指出2024年全市優質辦公樓平均租金面價同比下降約6.2%。
                                零售物業方面，儘管餐飲等特定業態表現出一定的需求韌性，但整體消費增長趨于謹慎，也對租金和空置情況構成挑戰。
                            </Text>
                            <Text mb="sm">
                                展望未來，世邦魏理仕預計廣州商業地產市場短期內仍將面臨供應高峰的壓力，空置率和租金水平可能持續承壓。
                            </Text>
                            <Text fz="xs" c="dimmed">
                                (來源: 世邦魏理仕《2024年廣州房地產市場回顧與展望》, 觀點網)
                            </Text>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 5 }}>
                            <MantineTitle order={4} mb="sm">區域發展趨勢探討</MantineTitle>
                            <Text mb="sm">
                                對於芳村等特定區域的商業發展趨勢，需要結合更詳細的區域規劃、人口流動、基礎設施建設以及具體項目數據進行深入分析。
                                宏觀市場報告（如世邦魏理仕的報告）主要提供全市層面的整體洞察，具體的次級市場表現可能存在差異。
                            </Text>
                            <Text fz="xs" c="dimmed">
                                此前展示的芳村與廣州市平均水平的多維度對比圖表已移除，以待更具體和可驗證的區域數據支援。
                            </Text>
                        </Grid.Col>
                    </Grid>
                </CardContent>
            </Card>
            <Grid>
                <Grid.Col span={{ base: 12, md: 6 }}>
                    <Card>
                        <CardHeader><CardTitle><Group gap="xs"><IconMapping.MapPin size={20}/>區域商舖價格對比</Group></CardTitle></CardHeader>
                        <CardContent pt="md">
                            <Paper h={280} p="sm" withBorder>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={districtComparisonData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="price" name="均價(元/㎡)" fill="#8884d8" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Paper>
                            <Box mt="md"><MantineTitle order={4} mb="sm">區域價格對比分析</MantineTitle><Text>從區域商舖價格對比來看，芳村區域商舖均價為37,800元/平方米，低於荔灣其他區域的39,500元/平方米，遠低於越秀區的45,000元/平方米、海珠區的42,000元/平方米和天河區的48,000元/平方米。這反映出芳村區域商業地產的市場價值相對較低，主要是由於區域商業發展水平和消費能力的差異。</Text></Box>
                        </CardContent>
                    </Card>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                    <Card>
                        <CardHeader><CardTitle><Group gap="xs"><IconMapping.Banknote size={20}/>區域租金水平對比</Group></CardTitle></CardHeader>
                        <CardContent pt="md">
                            <Paper h={280} p="sm" withBorder>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={districtComparisonData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="rental" name="月租金(元/㎡)" fill="#82ca9d" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Paper>
                            <Box mt="md"><MantineTitle order={4} mb="sm">區域租金對比分析</MantineTitle><Text>從區域租金水平對比來看，芳村區域商舖月租金為56元/平方米，低於荔灣其他區域的約58.67元/平方米，遠低於越秀區的約66.67元/平方米、海珠區的約61.33元/平方米和天河區的72元/平方米。這與區域商舖價格的差異基本一致，反映出區域商業價值的差異。</Text></Box>
                        </CardContent>
                    </Card>
                </Grid.Col>
            </Grid>
            <Card>
                <CardHeader><CardTitle><Group gap="xs"><IconMapping.Percent size={20}/>投資回報率對比</Group></CardTitle></CardHeader>
                <CardContent pt="md">
                    <Grid>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                            <MantineTitle order={4} mb="sm">區域回報率對比</MantineTitle>
                            <Paper h={280} p="sm" withBorder>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={districtComparisonData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="roi" name="租金回報率(%)" fill="#ff7300" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Paper>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                            <MantineTitle order={4} mb="sm">商舖類型回報率對比</MantineTitle>
                            <Paper h={280} p="sm" withBorder>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={shopTypeComparisonData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="roi" name="租金回報率(%)" fill="#ff7300" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Paper>
                        </Grid.Col>
                    </Grid>
                    <Box mt="lg"><MantineTitle order={4} mb="sm">回報率對比分析</MantineTitle><Text mb="sm">從區域回報率對比來看，芳村區域商舖租金回報率為6.6%，與荔灣其他區域的6.7%、越秀區的6.7%、海珠區的6.6%相近，略低於天河區的6.8%。這表明儘管不同區域的商舖價格和租金水平存在差異，但租金回報率相對接近，反映出市場對不同區域商舖的風險溢價要求基本一致。</Text><Text>從商舖類型回報率對比來看，小區內商舖的租金回報率為6.9%，略高於寫字樓底商的6.9%，與商場內商舖的7.0%相近，低於臨街商舖的7.1%。這表明小區內商舖雖然價格和租金水平較低，但租金回報率相對较高，具有一定的投資價值。</Text></Box>
                </CardContent>
            </Card>
             <Card>
                <CardHeader><CardTitle><Group gap="xs"><IconMapping.TrendingDown size={20}/>市場風險分析</Group></CardTitle></CardHeader>
                <CardContent pt="md">
                    <Grid>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                            <MantineTitle order={4} mb="sm">市場風險因素</MantineTitle>
                            <Stack gap="sm">
                                <Group gap="xs" align="flex-start"><IconMapping.AlertCircle size={20} color="red"/><Text><strong>電商衝擊：</strong>電子商務的快速發展對傳統零售業造成巨大衝擊，導致小區內商舖的經營壓力增大，租戶經營穩定性下降。</Text></Group>
                                <Group gap="xs" align="flex-start"><IconMapping.AlertCircle size={20} color="red"/><Text><strong>消費習慣改變：</strong>年輕消費者更傾向於在大型商場或線上購物，對小區內商舖的需求減少。</Text></Group>
                                <Group gap="xs" align="flex-start"><IconMapping.AlertCircle size={20} color="red"/><Text><strong>區域競爭加劇：</strong>周邊新興商圈的崛起分流了芳村區域的商業客流，導致區域商業活力下降。</Text></Group>
                                <Group gap="xs" align="flex-start"><IconMapping.AlertCircle size={20} color="red"/><Text><strong>經濟增長放緩：</strong>宏觀經濟增長放緩導致消費能力下降，商舖租金增長空間有限。</Text></Group>
                                <Group gap="xs" align="flex-start"><IconMapping.AlertCircle size={20} color="red"/><Text><strong>政策風險：</strong>房地產調控政策可能影響商業地產的交易活躍度和價格走勢。</Text></Group>
                            </Stack>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                            <MantineTitle order={4} mb="sm">市場機遇分析</MantineTitle>
                             <Stack gap="sm">
                                <Group gap="xs" align="flex-start"><IconMapping.CheckCircle2 size={20} color="green"/><Text><strong>社區商業需求穩定：</strong>居民對日常生活服務的需求相對穩定，如餐飲、便利店、生活服務等業態仍有市場。</Text></Group>
                                <Group gap="xs" align="flex-start"><IconMapping.CheckCircle2 size={20} color="green"/><Text><strong>新業態發展：</strong>社區團購、生鮮配送等新業態的發展為小區內商舖提供了新的經營方向。</Text></Group>
                                <Group gap="xs" align="flex-start"><IconMapping.CheckCircle2 size={20} color="green"/><Text><strong>租金回報率相對穩定：</strong>儘管價格增長放緩，但租金回報率相對穩定，仍高於銀行存款利率。</Text></Group>
                                <Group gap="xs" align="flex-start"><IconMapping.CheckCircle2 size={20} color="green"/><Text><strong>區域改造升級：</strong>芳村區域的城市更新和改造升級可能帶來商業環境的改善和價值提升。</Text></Group>
                                <Group gap="xs" align="flex-start"><IconMapping.CheckCircle2 size={20} color="green"/><Text><strong>政策支援：</strong>政府對社區商業的支援政策可能為小區內商舖帶來新的發展機遇。</Text></Group>
                            </Stack>
                        </Grid.Col>
                    </Grid>
                </CardContent>
            </Card>
        </Stack>
    );
}

export default MarketAnalysisPanel; 